import {
  motion,
} from "framer-motion";

import testimonialsData from "../../../site/data/testimonials";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  TESTIMONIALS
  ============================================================

  Desktop:
  - kolme editorial-palautetta rinnakkain

  Mobile:
  - swipe
  - snap scrolling

  Ei tähtiarvosteluja tai geneerisiä review-kortteja.
*/

function localized(
  value,
  language
) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  return (
    value[language] ??
    value.fi ??
    value.en ??
    ""
  );
}

export default function TestimonialsSection({
  language = "fi",
}) {
  const data =
    testimonialsData || {};

  if (
    data.enabled ===
    false
  ) {
    return null;
  }

  const section =
    data.section ||
    {};

  const items =
    (
      data.items ||
      []
    )
      .filter(
        (item) =>
          item.enabled !==
          false
      )
      .sort(
        (a, b) =>
          Number(
            a.order || 0
          ) -
          Number(
            b.order || 0
          )
      );

  if (
    items.length ===
    0
  ) {
    return null;
  }

  return (
    <section
      id="testimonials"
      className="
        relative
        overflow-hidden
        border-t
        border-border
        bg-surface
      "
    >
      <div
        className="
          mx-auto
          w-full
          max-w-editorial
          px-6
          py-24

          sm:px-10
          sm:py-32

          lg:px-16
          lg:py-40
        "
      >
        {/* ====================================================
            META
            ==================================================== */}

        <motion.div
          className="
            flex
            items-center
            justify-between
            gap-8
            border-b
            border-border
            pb-5
          "
          initial={{
            opacity: 0,
            y: 16,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.5,
          }}
          transition={{
            duration: 0.75,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <p
            className="
              m-0
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-muted

              sm:text-[10px]
            "
          >
            05 /{" "}
            {localized(
              section.eyebrow,
              language
            )}
          </p>

          <span
            className="
              h-px
              w-12
              bg-border

              sm:w-20
            "
            aria-hidden="true"
          />
        </motion.div>

        {/* ====================================================
            INTRO
            ==================================================== */}

        <div
          className="
            mt-14
            grid
            gap-10

            sm:mt-20

            md:grid-cols-[minmax(0,1fr)_minmax(280px,0.32fr)]
            lg:items-end
            lg:gap-20
          "
        >
          <motion.h2
            className="
              m-0
              max-w-5xl
              font-display
              text-[clamp(3.2rem,6.5vw,7rem)]
              font-normal
              leading-[0.9]
              tracking-[-0.05em]
              text-text
            "
            initial={{
              opacity: 0,
              y: 28,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{
              duration: 0.9,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            {localized(
              section.title,
              language
            )}
          </motion.h2>

          <motion.div
            className="
              max-w-md
            "
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.35,
            }}
            transition={{
              duration: 0.8,
              delay: 0.1,
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                section.description,
                language
              )}
            </p>
          </motion.div>
        </div>

        {/* ====================================================
            MOBILE SWIPE
            ==================================================== */}

        <div
          className="
            mt-14
            flex
            items-center
            justify-between
            border-b
            border-border
            pb-4

            md:hidden
          "
        >
          <span
            className="
              text-[9px]
              font-medium
              uppercase
              tracking-[0.22em]
              text-muted
            "
          >
            {localized(
              section.swipe,
              language
            )}
          </span>

          <span
            className="
              text-sm
              text-muted
            "
            aria-hidden="true"
          >
            ← →
          </span>
        </div>

        {/* ====================================================
            REVIEWS
            ==================================================== */}

        <div
          className="
            -mx-6
            mt-5
            flex
            snap-x
            snap-mandatory
            gap-4
            overflow-x-auto
            px-6
            pb-5

            sm:-mx-10
            sm:px-10

            md:mx-0
            md:mt-20
            md:grid
            md:grid-cols-3
            md:gap-0
            md:overflow-visible
            md:px-0
            md:pb-0
          "
          style={{
            scrollbarWidth:
              "none",
          }}
        >
          {items.map(
            (
              item,
              index
            ) => {
              const isLast =
                index ===
                items.length - 1;

              return (
                <motion.article
                  key={
                    item.id
                  }
                  className={`
                    flex
                    min-w-[84vw]
                    snap-start
                    flex-col
                    justify-between
                    border
                    border-border
                    bg-canvas
                    p-7

                    sm:min-w-[66vw]
                    sm:p-10

                    md:min-w-0
                    md:border-0
                    md:border-r
                    md:border-border
                    md:bg-transparent
                    md:px-6 lg:px-10
                    md:py-2

                    ${
                      index === 0
                        ? "md:pl-0"
                        : ""
                    }

                    ${
                      isLast
                        ? "md:border-r-0 lg:pr-0"
                        : ""
                    }
                  `}
                  initial={{
                    opacity: 0,
                    y: 28,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  transition={{
                    duration: 0.85,
                    delay:
                      index *
                      0.08,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                >
                  <div>
                    <div
                      className="
                        flex
                        items-start
                        justify-between
                        gap-8
                      "
                    >
                      <span
                        className="
                          font-display
                          text-[4.5rem]
                          font-normal
                          leading-[0.7]
                          text-muted/35

                          sm:text-[5.5rem]
                        "
                        aria-hidden="true"
                      >
                        “
                      </span>

                      <span
                        className="
                          pt-1
                          text-[9px]
                          font-medium
                          tracking-[0.18em]
                          text-muted
                        "
                        aria-hidden="true"
                      >
                        {
                          item.id
                        }
                      </span>
                    </div>

                    <blockquote
                      className="
                        m-0
                        mt-8
                        font-display
                        text-[clamp(1.75rem,2.5vw,2.65rem)]
                        font-normal
                        leading-[1.08]
                        tracking-[-0.025em]
                        text-text
                      "
                    >
                      {localized(
                        item.quote,
                        language
                      )}
                    </blockquote>
                  </div>

                  <div
                    className="
                      mt-12
                      border-t
                      border-border
                      pt-5
                    "
                  >
                    <p
                      className="
                        m-0
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-[0.2em]
                        text-text
                      "
                    >
                      {
                        item.name
                      }
                    </p>

                    <p
                      className="
                        mb-0
                        mt-2
                        text-[9px]
                        uppercase
                        tracking-[0.18em]
                        text-muted
                      "
                    >
                      {localized(
                        item.type,
                        language
                      )}
                    </p>
                  </div>
                </motion.article>
              );
            }
          )}
        </div>
      </div>
    </section>
  );
}